import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { authAPI, validateAPI, getAccessToken, getRefreshToken } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Shield, Loader2, Eye, EyeOff, Lock, Users, Key, CheckCircle2 } from 'lucide-react';

// Build the final callback URL with tokens appended as query params, preserving
// any existing query string on the redirect target. Returns null on malformed input.
function buildCallbackUrl(redirectUrl, accessToken, refreshToken) {
  try {
    const u = new URL(redirectUrl);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return null;
    u.searchParams.set('access_token', accessToken);
    u.searchParams.set('refresh_token', refreshToken);
    return u.toString();
  } catch {
    return null;
  }
}

export default function LoginPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login, user, loading: authLoading } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [justLoggedOut, setJustLoggedOut] = useState(false);

  const redirectParam = searchParams.get('redirect');

  // Surface the "you've been logged out" notice when the GET /api/auth/logout
  // endpoint redirected the user back here with ?logout=1. Strip the param
  // from the URL so a refresh doesn't re-trigger the notice.
  useEffect(() => {
    if (searchParams.get('logout') === '1') {
      setJustLoggedOut(true);
      const url = new URL(window.location.href);
      url.searchParams.delete('logout');
      window.history.replaceState({}, '', url.pathname + url.search + url.hash);
    }
  }, [searchParams]);

  // Perform the SSO redirect: backend whitelist + tool-access gatekeeper
  // check, build callback URL, full page nav. If anything fails, caller-
  // supplied fallback runs. When the backend returns reason="no_tool_access"
  // we surface the Dutch message so the user understands WHY redirect failed.
  const performSsoRedirect = useCallback(async (accessToken, refreshToken, onFail) => {
    if (!redirectParam) { onFail(); return; }

    // Fail fast on malformed URL without hitting the backend
    const probe = buildCallbackUrl(redirectParam, 'x', 'x');
    if (!probe) { onFail(); return; }

    try {
      const res = await validateAPI.validateRedirect(redirectParam);
      const data = await res.clone().json();
      if (!res.ok || !data?.allowed) {
        // Tool-access gatekeeper denied — show the Dutch message.
        if (data?.reason === 'no_tool_access') {
          toast.error(data.message || 'Geen toegang tot deze tool. Neem contact op met je beheerder.', {
            duration: 6000,
          });
        }
        onFail();
        return;
      }

      const callback = buildCallbackUrl(redirectParam, accessToken, refreshToken);
      if (!callback) { onFail(); return; }

      setRedirecting(true);
      window.location.href = callback;
    } catch {
      onFail();
    }
  }, [redirectParam]);

  // Already-authenticated (either via fresh login or via the same-origin
  // session cookie probe in AuthProvider): if ?redirect= is present, do the
  // SSO handoff to the tool. If no redirect param is present, send the user
  // straight to the dashboard so they don't sit on /login while logged in.
  useEffect(() => {
    if (authLoading || !user) return;
    const at = getAccessToken();
    const rt = getRefreshToken();
    if (redirectParam) {
      if (!at || !rt) return; // wait until tokens are populated
      performSsoRedirect(at, rt, () => navigate('/', { replace: true }));
    } else {
      navigate('/', { replace: true });
    }
  }, [user, authLoading, redirectParam, performSsoRedirect, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await login(email, password);
      if (redirectParam) {
        await performSsoRedirect(data.access_token, data.refresh_token, () => navigate('/', { replace: true }));
      } else {
        navigate('/', { replace: true });
      }
    } catch (err) {
      setError(err.message || 'Inloggen mislukt');
    } finally {
      setLoading(false);
    }
  };

  // While the AuthProvider is probing the same-origin session cookie, show a
  // minimal loading splash instead of flashing the login form. This avoids the
  // "form-flicker" when SSO will succeed and a redirect is imminent.
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#1a2332] text-white" data-testid="login-session-checking">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-[#d4a853]" />
          <p className="text-sm text-white/70">Sessie controleren...</p>
        </div>
      </div>
    );
  }

  // If a session restored a user, the useEffect above will navigate / SSO-redirect
  // imminently — render a transient splash instead of the form.
  if (user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#1a2332] text-white" data-testid="login-restoring">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-[#d4a853]" />
          <p className="text-sm text-white/70">
            {redirectParam ? 'Doorsturen naar tool...' : 'Doorsturen naar dashboard...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Left brand panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#1a2332] text-white relative overflow-hidden">
        {/* Strong gradient background */}
        <div className="absolute inset-0" style={{
          background: `
            radial-gradient(1000px circle at 30% 20%, rgba(212,168,83,0.25), transparent 50%),
            radial-gradient(800px circle at 70% 80%, rgba(212,168,83,0.12), transparent 50%),
            radial-gradient(600px circle at 80% 10%, rgba(255,255,255,0.04), transparent 40%)
          `
        }} />

        {/* Decorative elements */}
        <div className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full border border-white/[0.06]" />
        <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full border border-white/[0.06]" />
        <div className="absolute top-1/2 -left-16 w-48 h-48 rounded-full bg-[#d4a853]/[0.06]" />
        <div className="absolute bottom-32 right-20 w-24 h-24 rounded-full bg-[#d4a853]/[0.08]" />

        {/* Gold accent line */}
        <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-transparent via-[#d4a853]/40 to-transparent" />

        {/* Content — vertically and horizontally centered */}
        <div className="relative z-10 flex flex-col justify-center px-12 xl:px-16 w-full">
          {/* Logo */}
          <div className="flex items-center gap-4 mb-10">
            <div className="w-14 h-14 rounded-2xl bg-[#d4a853] flex items-center justify-center shadow-lg shadow-[#d4a853]/20">
              <Shield className="w-8 h-8 text-[#1a2332]" />
            </div>
            <div>
              <span className="text-2xl font-bold tracking-tight">TW1 Group</span>
              <p className="text-xs text-[#d4a853]/80 font-medium tracking-widest uppercase mt-0.5">Identity Platform</p>
            </div>
          </div>

          {/* Heading */}
          <h1 className="text-5xl xl:text-6xl font-bold tracking-tight leading-[1.1] mb-6">
            Centrale<br />
            <span className="text-[#d4a853]">toegang</span> voor<br />
            alle TW1 tools
          </h1>

          <p className="text-base text-slate-300 max-w-md leading-relaxed mb-12">
            Beheer accounts, sessies en rechten veilig op één plek. Eén login voor DailyOps, Admin Portaal, OpsHub en SmartRoster.
          </p>

          {/* Feature badges */}
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3 text-sm text-slate-300">
              <div className="w-8 h-8 rounded-lg bg-white/[0.07] flex items-center justify-center">
                <Lock className="w-4 h-4 text-[#d4a853]" />
              </div>
              <span>Beveiligd met JWT tokens</span>
            </div>
            <div className="flex items-center gap-3 text-sm text-slate-300">
              <div className="w-8 h-8 rounded-lg bg-white/[0.07] flex items-center justify-center">
                <Users className="w-4 h-4 text-[#d4a853]" />
              </div>
              <span>Rolgebaseerde toegangscontrole</span>
            </div>
            <div className="flex items-center gap-3 text-sm text-slate-300">
              <div className="w-8 h-8 rounded-lg bg-white/[0.07] flex items-center justify-center">
                <Key className="w-4 h-4 text-[#d4a853]" />
              </div>
              <span>API-sleutels per tool</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right form panel */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-4 py-10 bg-[#f8fafc]">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-xl bg-[#1a2332] flex items-center justify-center">
              <Shield className="w-6 h-6 text-[#d4a853]" />
            </div>
            <span className="text-xl font-bold text-[#1a2332]">TW1 Group</span>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
            <h2 className="text-2xl font-semibold text-[#1a2332] mb-1">Inloggen</h2>
            <p className="text-sm text-slate-500 mb-6">Log in op het TW1 Identity Platform</p>

            {justLoggedOut && (
              <div
                className="mb-4 flex items-start gap-2 bg-emerald-50 border border-emerald-100 text-emerald-900 text-sm rounded-lg px-3 py-2"
                data-testid="login-logout-notice"
              >
                <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0 text-emerald-600" />
                <span>Je bent uitgelogd.</span>
              </div>
            )}

            {error && (
              <Alert variant="destructive" className="mb-4" data-testid="login-error-alert">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">E-mailadres</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="naam@truckwash1group.nl"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  data-testid="login-email-input"
                  className="h-11"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Wachtwoord</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Je wachtwoord"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    data-testid="login-password-input"
                    className="h-11 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors duration-150"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="flex justify-end">
                <Link
                  to="#"
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById('forgot-dialog')?.click();
                  }}
                  className="text-sm text-[#d4a853] hover:text-[#c79b45] transition-colors duration-150"
                  data-testid="login-forgot-password-link"
                >
                  Wachtwoord vergeten?
                </Link>
              </div>

              <Button
                type="submit"
                disabled={loading || redirecting}
                className="w-full h-11 bg-[#1a2332] hover:bg-[#111827] text-white rounded-xl btn-press transition-colors duration-150"
                data-testid="login-submit-button"
              >
                {redirecting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Doorsturen naar tool...</>
                ) : loading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Inloggen...</>
                ) : (
                  'Inloggen'
                )}
              </Button>
            </form>
          </div>

          {/* Forgot password modal */}
          <ForgotPasswordModal />

          <p className="text-center text-xs text-slate-400 mt-6">id.truckwash1.cloud</p>
        </div>
      </div>
    </div>
  );
}

function ForgotPasswordModal() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await authAPI.forgotPassword(email);
      setSent(true);
    } catch {} finally {
      setLoading(false);
    }
  };

  if (!open) {
    return (
      <button
        id="forgot-dialog"
        className="hidden"
        onClick={() => setOpen(true)}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => { setOpen(false); setSent(false); }}>
      <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
        {sent ? (
          <div className="text-center py-4">
            <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
              <Shield className="w-6 h-6 text-emerald-600" />
            </div>
            <h3 className="text-lg font-semibold text-[#1a2332] mb-2">E-mail verzonden</h3>
            <p className="text-sm text-slate-500">Als het e-mailadres bekend is, ontvang je een reset link.</p>
            <Button onClick={() => { setOpen(false); setSent(false); }} className="mt-4 bg-[#1a2332] hover:bg-[#111827] text-white rounded-xl">
              Sluiten
            </Button>
          </div>
        ) : (
          <>
            <h3 className="text-lg font-semibold text-[#1a2332] mb-1">Wachtwoord vergeten</h3>
            <p className="text-sm text-slate-500 mb-4">Voer je e-mailadres in om een reset link te ontvangen.</p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                type="email"
                placeholder="naam@truckwash1group.nl"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-11"
              />
              <div className="flex gap-3">
                <Button type="button" variant="outline" onClick={() => setOpen(false)} className="flex-1 rounded-xl">
                  Annuleren
                </Button>
                <Button type="submit" disabled={loading} className="flex-1 bg-[#1a2332] hover:bg-[#111827] text-white rounded-xl">
                  {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Bezig met verzenden...</> : 'Versturen'}
                </Button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
